/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Argon Dashboard 2 MUI context
import {
  useArgonController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import axios, { baseURL } from "utils/api";

const formatRelativeTime = (value) => {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Recently";

  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (elapsedSeconds < 60) return "Just now";
  if (elapsedSeconds < 3600) return `${Math.floor(elapsedSeconds / 60)} min ago`;
  if (elapsedSeconds < 86400) return `${Math.floor(elapsedSeconds / 3600)} hr ago`;
  return `${Math.floor(elapsedSeconds / 86400)} day ago`;
};

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await axios.get(`${baseURL}/notification/admin-feed`, {
        params: { limit: 4 },
      });
      setNotifications((response.data.data || []).slice(0, 4));
      setUnseenCount(response.data.unseenCount || 0);
    } catch (error) {
      console.error("Unable to fetch admin notifications:", error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 30000);
    window.addEventListener("focus", fetchNotifications);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", fetchNotifications);
    };
  }, []);

  const handleOpenMenu = async (event) => {
    setOpenMenu(event.currentTarget);
    await fetchNotifications();
    try {
      await axios.put(`${baseURL}/notification/admin-feed/mark-seen`);
      setUnseenCount(0);
      setNotifications((items) => items.map((item) => ({ ...item, seen: true })));
    } catch (error) {
      console.error("Unable to mark admin notifications as seen:", error);
    }
  };
  const handleCloseMenu = () => setOpenMenu(false);

  const handleNotificationClick = (notification) => {
    handleCloseMenu();
    if (notification.redirectParams?.redirectionType === "SUPPORT_TICKET") {
      navigate("/supportticket", {
        state: { ticketId: notification.redirectParams.redirectionId },
      });
    }
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
      PaperProps={{ sx: { width: 380, maxWidth: "calc(100vw - 24px)" } }}
    >
      {notificationsLoading && notifications.length === 0 ? (
        <ArgonBox display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </ArgonBox>
      ) : null}
      {!notificationsLoading && notifications.length === 0 ? (
        <ArgonBox px={3} py={2}>
          <ArgonTypography variant="button" color="secondary">
            No notifications yet
          </ArgonTypography>
        </ArgonBox>
      ) : null}
      {notifications.map((notification) => (
        <NotificationItem
          key={notification._id}
          color={notification.seen ? "secondary" : "warning"}
          image={
            <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
              support_agent
            </Icon>
          }
          title={[notification.title || "Notification", notification.description || ""]}
          date={
            notification.client?.name
              ? `${notification.client.type}: ${notification.client.name} · ${formatRelativeTime(
                  notification.createdAt,
                )}`
              : formatRelativeTime(notification.createdAt)
          }
          onClick={() => handleNotificationClick(notification)}
        />
      ))}
      {notifications.length > 0 ? (
        <ArgonBox
          component='button'
          type='button'
          onClick={() => {
            handleCloseMenu();
            navigate('/notification');
          }}
          sx={{
            width: '100%',
            py: 1.5,
            border: 0,
            borderTop: '1px solid #e9ecef',
            background: 'transparent',
            color: '#344767',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            textAlign: 'center',
            '&:hover': { backgroundColor: '#f8f9fa' },
          }}
        >
          View more
        </ArgonBox>
      ) : null}
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme, { navbarType })}>
        <ArgonBox
          color={light && transparentNavbar ? "white" : "white"}
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={transparentNavbar ? light : false}
          />
          <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </ArgonBox>
        {/* <Dropdown
          value={state}
          options={["Bhopal", "Indore", "Gwalior", "Jabalpur", "Ujjain", "Sagar"]} // Example options
          onChange={(e)=>setState(e.target.value)}
         /> */}
        {isMini ? null : (
          <ArgonBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* <Dropdown
          value={state}
          options={["Bhopal", "Indore", "Gwalior", "Jabalpur", "Ujjain", "Sagar"]} // Example options
          onChange={(e)=>setState(e.target.value)}
           width="210px"
          
         /> */}
            {/* <ArgonBox pl={2} pr={1}>
              <ArgonInput
                placeholder="Type here..."
                startAdornment={
                  <Icon fontSize="small" style={{ marginRight: "6px" }}>
                    search
                  </Icon>
                }
              />
            </ArgonBox> */}
            <ArgonBox color={light ? "white" : "inherit"}>
              <Link to="/profile">
                <IconButton sx={navbarIconButton} size="small">
                  <Icon
                    sx={({ palette: { dark, white } }) => ({
                      color: light && transparentNavbar ? white.main : white.main,
                    })}
                  >
                    account_circle
                  </Icon>
                  <ArgonTypography
                    variant="button"
                    fontWeight="medium"
                    color={light && transparentNavbar ? "white" : "white"}
                  >
                    Profile
                  </ArgonTypography>
                </IconButton>
              </Link>
              <IconButton
                size="small"
                color={light && transparentNavbar ? "white" : "white"}
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
              </IconButton>
              <IconButton
                size="small"
                color={light && transparentNavbar ? "white" : "white"}
                sx={{ ...navbarIconButton, display: "none" }}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                color={light && transparentNavbar ? "white" : "white"}
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge badgeContent={unseenCount} color="error" max={99}>
                  <Icon>notifications</Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </ArgonBox>
          </ArgonBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: true,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
